import React, { useState } from 'react';
import { useRouterParam } from '../../../model/history';
import { useRemoteData } from '../../../model/data';
import { ControlledModal } from '../../../components/Modal/ControlledModal';
import { Container } from '../../../components/Container/Container';
import { Card } from '../../../components/Card/Card';
import { RequestAgreement, ContractInfo } from '../../../model/contract';
import { contractsPath } from '../../../routes';
import { getPageTitle, useMeta } from '../../../model/meta';
import { LoadingPage } from '../../../components/LoadingPage/LoadingPage';
import { AddAgreement } from '../modals/AddAgreement';
import { getContract } from '../../../model/contract';
import { SortableList } from '../../../components/SortableList/SortableList';
import { Tag } from '../../../components/Tag/Tag';
import { Shop } from '../../../model/shop';
import './ContractDetails.css';
import { formatDateTime, formatPercentage, useReloadKey, formatCurrency, present } from '../../../util/util';
import { t } from '../../../locales/translate';
import { PermissionButton } from '../../../components/Button/PermissionButton';
import { ContractForm } from '../modals/ContractForm';
import { Activities } from '../../../components/Activities/Activities';
import { Stack } from '../../../components/Stack/Stack';

export function ContractDetails(): JSX.Element {
  const { id } = useRouterParam<{ id: string }>();
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showAddAgreement, setShowAddAgreement] = useState(false);
  const [activitiesKey, reloadActivities] = useReloadKey();

  const [contract, loading, error] = useRemoteData(
    () => getContract(id),
    (db) => db.table('contracts').find(id),
    [id]
  );

  useMeta({
    title: getPageTitle(loading, `${t.model.contract._name} ${contract && contract.reference}`, contract),
    breadcrumbs: [
      { title: t.model.contract._plural, url: contractsPath() },
      {
        title: getPageTitle(loading, `${t.model.contract._name} ${contract && contract.reference}`, contract),
      },
    ],
  });

  if (loading) {
    return <LoadingPage title={t.actions.loading(t.model.contract._name)} />;
  } else if (error || !contract) {
    return <Container>{t.errors.itemNotFound(t.model.contract._name)}</Container>;
  }

  const agreements = contract.contractAgreements.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()) || [];

  return (
    <Container className="contract-details">
      <Stack size="lg">
        <Card
          elevation={2}
          fullbleed
          className="contract-details__general-details"
          actions={
            <PermissionButton name="edit" model={contract} permission="update" onClick={() => setShowInfoModal(true)} />
          }
        >
          <div className="contract-details__details">
            <h1 className="contract-details__name">{contract.name}</h1>
            <h1 className="contract-details__info">
              <span className="contract-details__reference">{contract.reference}</span>
              {<Tag text={t.model.status[contract.status]} />}
              {contract.archived && <Tag text={t.general.archived} className="contract-details__archived" />}
            </h1>
          </div>
        </Card>

        <Card
          title={t.model.agreement._plural}
          actions={
            <PermissionButton
              name="plus"
              model={contract}
              permission="update"
              onClick={() => setShowAddAgreement(true)}
            />
          }
          elevation={2}
          fullbleed
        >
          <SortableList
            items={agreements}
            emptyMessage={t.errors.noItemsforThisCollection(t.model.agreement._plural, t.model.contract._name)}
            columns={[
              {
                dataKey: 'name',
                label: t.general.name,
              },
              {
                dataKey: 'shops',
                label: t.model.shop._plural,
                cellRenderer: (row) =>
                  (row.shops && (row.shops as Shop[]).map((shop) => shop.name).join(', ')) ||
                  `${t.general.all} ${t.model.shop._plural}`,
              },
              {
                dataKey: 'createdAt',
                label: t.model.datetime.createdAt,
                cellRenderer: (row) => formatDateTime(row.createdAt),
              },
              {
                dataKey: 'invalidatedAt',
                label: t.model.datetime.invalidatedAt,
                cellRenderer: (row) => formatDateTime(row.invalidatedAt),
              },
            ]}
            expandable={(row) => !!row.contractAgreementLines && row.contractAgreementLines.length > 0}
            expandedContent={(row) => (
              <SortableList
                items={row.contractAgreementLines}
                columns={[
                  agreements.some((agreement) =>
                    agreement.contractAgreementLines?.some((line) => present(line.description))
                  ) && {
                    dataKey: 'description',
                    label: t.model.agreement.description,
                  },
                  {
                    dataKey: 'companyId',
                    label: t.model.agreement.partner,
                    cellRenderer: (innerRow) => {
                      const company = contract.companies.find((item) => item.id === innerRow.companyId);
                      return company ? company.name : '';
                    },
                  },
                  {
                    dataKey: 'amount',
                    label: t.general.amount,
                    cellRenderer: (innerRow) =>
                      [
                        innerRow.contractType === 'percentage'
                          ? formatPercentage(innerRow.amount)
                          : formatCurrency(innerRow.amount),
                        innerRow.postTax && t.model.contract.postTax,
                      ]
                        .filter(Boolean)
                        .join(' '),
                  },
                  {
                    dataKey: 'vatOverride',
                    label: t.model.agreement.vatOverride,
                    cellRenderer: (innerRow) =>
                      innerRow.vatOverride ? formatPercentage(innerRow.vatOverride) : t.general.no,
                  },
                ]}
              />
            )}
          />
        </Card>
        <Activities subjectType="contract" subjectId={contract.id} cacheKey={activitiesKey} />
      </Stack>
      <ControlledModal<ContractInfo>
        open={showInfoModal}
        overflow={true}
        model={contract}
        title={t.actions.edit(t.model.contract._name)}
        onClose={() => setShowInfoModal(false)}
        size="small"
      >
        {(controller) => (
          <ContractForm updatingContractId={contract.id} controller={controller} onSuccess={() => reloadActivities()} />
        )}
      </ControlledModal>
      <ControlledModal<RequestAgreement>
        open={showAddAgreement}
        model={{} as RequestAgreement}
        onClose={() => {
          setShowAddAgreement(false);
        }}
        title={t.actions.create(t.model.agreement._name)}
        size="small"
      >
        {(controller) => <AddAgreement contractId={contract.id} controller={controller} />}
      </ControlledModal>
    </Container>
  );
}
